<template>
    <div class="rounded-2xl border border-app bg-card/70 backdrop-blur-2xl divide-y divide-app">
        <div v-for="row in rows" :key="row.ticket.id || row.ticket.title"
            class="flex items-center justify-between gap-4 p-4">
            <div class="min-w-0 flex items-center gap-2">
                <div class="min-w-0">
                    <p class="text-header font-semibold text-sm truncate">{{ row.ticket.title }}</p>
                    <p class="text-body/80 text-xs font-bold uppercase tracking-wider">
                        {{ priceLabel(row.ticket) }}<span v-if="row.ticket.price > 0"> / ticket</span>
                    </p>
                </div>
                <span v-if="row.ticket.featured"
                    class="shrink-0 px-2 py-0.5 rounded-full bg-brand-500 text-white text-[10px] font-bold uppercase tracking-widest">
                    Popular
                </span>
            </div>

            <div v-if="isSoldOut(row.ticket)" class="shrink-0 text-body/80 text-xs font-bold uppercase tracking-wider">
                Sold out
            </div>
            <div v-else class="flex items-center gap-2 shrink-0">
                <button type="button" @click="emit('update:qty', row.ticket, row.quantity - 1)" :disabled="row.quantity <= 0"
                    :aria-label="`Remove one ${row.ticket.title} ticket`"
                    class="h-7 w-7 rounded-lg border border-app text-header flex items-center justify-center hover:bg-muted/10 transition-colors disabled:opacity-40">
                    <Minus class="h-3.5 w-3.5" />
                </button>
                <span class="w-5 text-center font-bold text-header tabular-nums text-sm">{{ row.quantity }}</span>
                <button type="button" @click="emit('update:qty', row.ticket, row.quantity + 1)" :disabled="row.quantity >= maxQty"
                    :aria-label="`Add one ${row.ticket.title} ticket`"
                    class="h-7 w-7 rounded-lg border border-app text-header flex items-center justify-center hover:bg-muted/10 transition-colors disabled:opacity-40">
                    <Plus class="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Plus, Minus } from 'lucide-vue-next';
import { EventTicketType } from '~/types';
import { formatMoney } from '../helpers';

defineProps<{
    rows: { ticket: EventTicketType; quantity: number }[];
    maxQty: number;
}>();

const emit = defineEmits<{ (e: 'update:qty', ticket: EventTicketType, quantity: number): void }>();

const isSoldOut = (t: EventTicketType) => t.soldOut === true || t.available === false;
const priceLabel = (t: EventTicketType) => formatMoney(t.price, t.currency);
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.divide-app> :not([hidden])~ :not([hidden]) {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
